import React, {
    useCallback,
    useEffect,
    useState,
    useContext
} from 'react';
import _ from 'lodash';
import appConfig from 'app-config';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import { MLDriverVehicleUtil, MLNotificationBlockUtil } from 'ml-portals-util-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { MLPAAgentEditVehicleComponent, MLPartialVehicleDetailsComponent, MLPAEditVehicleComponent, MLModifierComponent } from 'ml-components-common-react';
import { FormModalPopover, platformMessages as commonMessages } from 'ml-components-platform-react';
import { MLPolicyChangeDataHolderService, MLNavigationStepsTrackingService } from 'ml-services-common-react';
import { MLEndorsementFlowUtil, MLEndorsementUtil, MLModifierUtil } from 'ml-portals-util-js';
import { useTranslator } from '@jutro/locale';
import { useModal } from '@jutro/components';
import { GatewayEndorsementService } from 'gw-capability-gateway-policychange';
import PolChangeTrackEvent from 'gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PolChangeTrackEvent.jsx';
import metadata from './VehiclesPagePolChange.metadata.json5';

import messages from './VehiclesPagePolChange.messages';
import styles from './VehiclesPagePolChange.module.scss';
import { ScrollToError } from '@jutro/legacy/wizard-next';

function VehiclesPagePolChange(props) {
    const modalApi = useModal();
    const {
        wizardData,
        updateWizardData,
        goNext,
        currentStepIndex,
        currentStep,
        steps,
        changeNextSteps,
    } = props;
    const { submissionVM, alertMessages = [] } = wizardData;
    const { EndorsementService } = useDependencies('EndorsementService');
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();

    const { authHeader } = useAuthentication();
    const { onValidate } = useValidation('VehiclesPagePolChange');

    const isGPA = appConfig.portalCode === 'GPA';
    const isAMP = appConfig.portalCode === 'AMP';
    const VEHICLES_PATH = 'lobData.personalAuto.coverables.vehicles';
    const paCoverableVal = _.get(submissionVM, 'lobData.personalAuto.coverables.value', {});
    const vehicleList = _.get(submissionVM, 'lobData.personalAuto.coverables.vehicles.children', []);
    const vehiclesVal = _.get(submissionVM, `${VEHICLES_PATH}.value`, {});
    const productCode = _.get(submissionVM, 'baseData.productCode.value', '');

    const [currentVehicleVM, setCurrentVehicleVM] = useState(viewModelService.create({}, 'pc', 'edge.capabilities.policyjob.lob.personalauto.coverables.dto.VehicleDTO'));
    const [isAddOrReplace, setIsAddOrReplace] = useState(false);
    const [isReplace, setIsReplace] = useState(false);
    const [isloaded, setIsLoaded] = useState(false);
    const effectiveDate = _.get(submissionVM, "baseData.effectiveDate.value");
    const policyAddress = _.get(submissionVM, "baseData.policyAddress");
    const [nonEditingVehicles, setNonEditingVehicles] = useState([]);
    const [commuteWarningDisplayed, setCommuteWarningDisplayed] = useState(false);
    const [replacedFixedId, setReplacedFixedId] = useState('');
    const alertNames = {
        commutedVehicleName: 'commutedVehicle',
        duplicatedVehicleName: 'duplicatedVehicle',
        onlyTrailerName: 'onlyTrailer'
    };
    const [reload, setReload] = useState(true);
    const [showErrors, setShowErrors] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);
    const [showErrorMessagetoAddInterestedParty, setShowErrorMessagetoAddInterestedParty] = useState(false);
    const isModernProduct = _.get(submissionVM, "isModernProduct_Ext.value", false);
    const [registeredOwnersValue, setRegisteredOwnersValue] = useState([]);
    const [showErrorMessageForStorageAsTheOnlyVehicle, setErrorMessageForStorageAsTheOnlyVehicle] = useState(false);
    const [vinInvalidCharactersError, setVinInvalidErrorMessage] = useState(null);
    const [vinNinthCharacterError, setVinNinthCharErrorMessage] = useState(null);
    const [vinTenthCharacterError, setVinTenthCharErrorMessage] = useState(null);
    const [vehiclePurchaseDateError, setVehiclePurchaseDateError] = useState(null);
    const [vinErrorCheck, updateVinErrorCheck] = useState(undefined);

    const {
        clearBlockAlerts,
        addErrorAlert,
        addWarningAlert,
        clearAlerts,
        addSuccessAlert,
        clearErrorAlerts
    } = MLNotificationBlockUtil();

    const {
        showProgressDialog,
        hideProgressDialog
    } = MLModalService();

    const {
        validateAntiqueVehicleAge,
        validateClassicVehicleAge
    } = MLDriverVehicleUtil();

    const updateWizardSubmissionData = useCallback((vm) => {
        _.set(wizardData, 'submissionVM', vm);
        updateWizardData(wizardData);
    }, [updateWizardData, wizardData]);

    const setFixIdsOfNonEditingVehicles = (lobVM) => {
        const nonEditingVehiclesArr = [];
        if (lobVM.personalAuto) {
            lobVM.personalAuto.coverables.vehicles.value.forEach((vhc) => {
                if (vhc.isSelected !== true) {
                    nonEditingVehiclesArr.push(vhc.fixedId);
                }
            });
        }
        setNonEditingVehicles(nonEditingVehiclesArr);
    };

    const removeMessage = (message, shouldRemove) => {
        if (shouldRemove) {
            const idx = _.indexOf(alertMessages, message);
            if (idx >= 0) {
                alertMessages.splice(idx, 1);
            }
        }
    };

    useEffect(() => {
        if (isGPA) {
            PolChangeTrackEvent('Endorsement Step Reached', false, submissionVM, authHeader, { step: 'PolChange Vehicles Screen' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showChangesAlerts = (shouldRemoveMessage = true) => {
        clearAlerts();
        if (alertMessages && alertMessages.length > 0) {
            alertMessages.forEach((alert) => {
                if (alert.alertName && (alert.message || alert.formattedMessageName)) {
                    let alertMessage = messages[alert.formattedMessageName];
                    if (alertMessage) {
                        addSuccessAlert({
                            ...messages[alert.formattedMessageName], args: {
                                strongName: (<strong>{alert.messagePram}</strong>),
                            }
                        }, null, null)
                    }
                }
                removeMessage(alert, shouldRemoveMessage)
            });

        }
    };
    const setCurrentVehicleVMInit = (lobVM) => {
        let idx;
        const result = {};
        if (lobVM.personalAuto) {
            if (isAMP) {
                let _currentVehicleVM;
                switch (MLPolicyChangeDataHolderService.getPageAction()) {
                    case 'add':
                        idx = lobVM.personalAuto.coverables.vehicles.value.indexOf(MLPolicyChangeDataHolderService.getVehicle());
                        _currentVehicleVM = lobVM.personalAuto.coverables.vehicles.getElement(idx);
                        _currentVehicleVM.value.toComplete_ML = true;
                        _currentVehicleVM.value.editMode_ML = true;
                        result.isAddOrReplace = true;
                        setIsAddOrReplace(true);

                        // Not part of the original code, workaround for using the wrong vehicle DTO
                        _currentVehicleVM.value.primaryUse = undefined;
                        _currentVehicleVM.value.ownedLessThan6Months_Ext = undefined;
                        _currentVehicleVM.value.parkingType_ML = undefined;
                        _currentVehicleVM.value.purchaseDate_ML = undefined;
                        _currentVehicleVM.value.financeCategory_ML = "owned";
                        _currentVehicleVM.value.annualMileage = undefined;
                        // End workaround
                        break;
                    case 'edit':
                        idx = lobVM.personalAuto.coverables.vehicles.children.findIndex((vhc) => {
                            return vhc.isSelected === true;
                        }
                        );
                        _currentVehicleVM = lobVM.personalAuto.coverables.vehicles.getElement(idx);
                        _currentVehicleVM.value.toComplete_ML = true;
                        _currentVehicleVM.value.editMode_ML = true;
                        setIsAddOrReplace(false);
                        result.isAddOrReplace = false;
                        break;
                    case 'editandreplace':
                        idx = lobVM.personalAuto.coverables.vehicles.value.indexOf(MLPolicyChangeDataHolderService.getVehicle());
                        _currentVehicleVM = lobVM.personalAuto.coverables.vehicles.children[idx]
                        _currentVehicleVM.value.toComplete_ML = true;
                        _currentVehicleVM.value.editMode_ML = true;
                        setIsAddOrReplace(false);
                        result.isAddOrReplace = false;
                        break;
                    case 'replace':
                        idx = lobVM.personalAuto.coverables.vehicles.value.indexOf(MLPolicyChangeDataHolderService.getVehicle());
                        _currentVehicleVM = lobVM.personalAuto.coverables.vehicles.getElement(idx)
                        setIsAddOrReplace(true);
                        setIsReplace(true);
                        result.isAddOrReplace = true;
                        result.isReplace = true;

                        // Not part of the original code, workaround for using the wrong vehicle DTO
                        _currentVehicleVM.value.primaryUse = undefined;
                        _currentVehicleVM.value.ownedLessThan6Months_Ext = undefined;
                        _currentVehicleVM.value.parkingType_ML = undefined;
                        _currentVehicleVM.value.purchaseDate_ML = undefined;
                        _currentVehicleVM.value.financeCategory_ML = "owned";
                        _currentVehicleVM.value.annualMileage = undefined;
                        // End workaround
                        break;
                    default:
                        break;
                }
                if (_currentVehicleVM) {
                    _currentVehicleVM.value.isAnnualMileageRequired_ML = true;
                    _currentVehicleVM.value.makeAddlInfoReq_Ext = false;
                    setCurrentVehicleVM(_currentVehicleVM);
                    result.currentVehicleVM = _currentVehicleVM;
                }
            } else {
                let index = lobVM.personalAuto.coverables.vehicles.children.findIndex((vhc) => {
                    return (vhc.isSelected === true || vhc.value.isSelected === true);
                });
                if (index === -1) {
                    index = lobVM.personalAuto.coverables.vehicles.children.findIndex((vhc) => {
                        if (vhc.value.fixedId === currentStep.fixedId) {
                            vhc.value.isSelected = true;
                            return true;
                        }
                        return false;
                    });
                }
                const _currentVehicleVM = lobVM.personalAuto.coverables.vehicles.getElement(index);
                if (_currentVehicleVM) {
                    currentVehicleVM.value.toComplete_ML = true;
                    currentVehicleVM.value.makeAddlInfoReq_Ext = false;
                    setCurrentVehicleVM(_currentVehicleVM);
                    result.isReplace = _currentVehicleVM.value.isReplacing;
                    result.currentVehicleVM = currentVehicleVM;
                } else {
                    currentVehicleVM.value.toComplete_ML = true;
                    currentVehicleVM.value.makeAddlInfoReq_Ext = false;
                    setCurrentVehicleVM(currentVehicleVM);
                    result.isReplace = currentVehicleVM.value.isReplacing;
                    result.currentVehicleVM = _currentVehicleVM;
                }
            }
        }

        return result;
    };

    const isVehicleAddOrReplace = () => {
        switch (MLPolicyChangeDataHolderService.getPageAction()) {
            case 'add':
                setIsAddOrReplace(true)
                break
            case 'replace':
                setIsAddOrReplace(true)
                setIsReplace(true)
                break
            default:
                setIsAddOrReplace(false)
                setIsReplace(false)
                break
        }
    };

    const setEditModeInit = useCallback((enabled) => {
        const paLine = submissionVM.lobData.personalAuto.value;
        if (paLine) {
            // eslint-disable-next-line no-return-assign
            submissionVM.lobData.personalAuto.value.coverables.vehicles.forEach((vehicle) => vehicle.editMode_ML = !!enabled)
        }
    }, [submissionVM.lobData.personalAuto])

    useEffect(() => {
        if (_.get(submissionVM, 'status.value.code') === 'Quoted' && isAMP) {
            return;
        }

        if (!(_.isUndefined(currentVehicleVM.value))) {
            if (!(_.isEmpty(currentVehicleVM.value))) {
                setIsLoaded(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentVehicleVM]);

    const initVehiclePolChangePage = () => {
        showChangesAlerts();
        if (alertMessages && alertMessages.length > 0) {
            const poppedAlert = alertMessages.pop();
            clearBlockAlerts(poppedAlert.alertName);
        }

        if (currentStep.pageAction) {
            MLPolicyChangeDataHolderService.setPageAction(currentStep.pageAction);
        }

        const fixedId = currentStep.replacedFixedId || MLPolicyChangeDataHolderService.getReplacedFixedId();
        if (MLPolicyChangeDataHolderService.getPageAction() === 'replace' && !!fixedId) {
            setReplacedFixedId(fixedId);
            MLPolicyChangeDataHolderService.setReplacedFixedId('');
        }

        setCurrentVehicleVMInit(submissionVM.lobData);
        setFixIdsOfNonEditingVehicles(submissionVM.lobData);

        isVehicleAddOrReplace();
        let stepName;
        switch (MLPolicyChangeDataHolderService.getPageAction()) {
            case 'add':
                stepName = messages.addVehicle;
                break;
            case 'edit':
                stepName = messages.editVehicle;
                break;
            case 'editandreplace':
                stepName = messages.editVehicle;
                break;
            case 'replace':
                stepName = messages.replaceVehicle;
                break;
            default:
                break;
        }
        stepName = translator(stepName);

        MLNavigationStepsTrackingService.addToDynamicSteps(stepName, !currentStep.existingStep, currentStep.mlStepName, authHeader);
        setEditModeInit(true);
    };

    useEffect(() => {
        if (MLEndorsementFlowUtil.startingPointPageLoadIsSkipping(submissionVM, goNext, steps, currentStepIndex, changeNextSteps)) {
            return;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (_.get(submissionVM, 'status.value.code') === 'Quoted' && isAMP) {
            return;
        }

        if (reload) {
            setReload(true);
            initVehiclePolChangePage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reload])

    const missingField = () => {
        return !(currentVehicleVM && currentVehicleVM.aspects.valid && currentVehicleVM.aspects.subtreeValid);

    }

    const hasMissingFields = () => {
        if (missingField()) {
            clearBlockAlerts('missingFields')
            const alertMsg = translator(messages.missingFields)
            setShowErrors(true)
            setErrorTimestamp(errorTimestamp + 1);
            addErrorAlert(alertMsg, 'missingFields')
            window.scrollTo(0, 0)
            return true;
        }
        else {
            clearBlockAlerts('missingFields')
            return false;
        }
    }

    const checkForOnlyTrailer = () => {
        const isOnlyTrailer = vehicleList.every((vehicle) => { return vehicle.category_ML === 'trailer'; })
        return isOnlyTrailer;
    }

    const checkForOnlyTrailerAlert = () => {
        const index = alertMessages.findIndex((a) => a.id === alertNames.onlyTrailerName);
        if (index >= 0) {
            alertMessages.splice(index, 1);
        }
        if (currentVehicleVM.hasOnlyTrailers) {
            alertMessages.push({
                type: 'error',
                id: alertNames.onlyTrailerName,
                message: translator(messages.trailerCannotBeTheOnlyVehicle)
            });
        }
    };

    const checkForDuplicateVins = (vin) => {
        let vinCount = 0;
        if (vin) {
            vehicleList.forEach((veh) => {
                if (veh.value.vin === vin) {
                    vinCount++;
                }
            });
        }
        return vinCount > 1;
    };

    const checkForDuplicateVehicleAlert = () => {
        const index = alertMessages.findIndex((a) => a.id === alertNames.duplicatedVehicleName);
        if (index >= 0) {
            alertMessages.splice(index, 1);
        }
        if (currentVehicleVM.hasDuplicateVins) {
            clearBlockAlerts('duplicateVin')
            const alertMsg = translator(messages.duplicateVinForVehicle)
            addErrorAlert(alertMsg, 'duplicateVin')
            window.scrollTo(0, 0)
        } else {
            clearBlockAlerts('duplicateVin')
        }
    };

    const onNextValidateAntiqueVehicleAge = () => {
        const antiqueVehicleAgeError = validateAntiqueVehicleAge(currentVehicleVM, effectiveDate);
        const antiqueVehicleAgeErrorValue = antiqueVehicleAgeError.hasError;
        clearErrorAlerts();
        if (antiqueVehicleAgeError.hasError) {
            const alertMsg = translator(antiqueVehicleAgeError.errorMessage);
            addErrorAlert(alertMsg, 'antiqueVehicleAgeError');
        }
        return antiqueVehicleAgeErrorValue;
    };

    const onNextvalidateClassicVehicleAge = () => {
        const classicVehicleAgeError = validateClassicVehicleAge(currentVehicleVM, effectiveDate);
        const classicVehicleAgeErrorValue = classicVehicleAgeError.hasError;
        clearErrorAlerts();
        if (classicVehicleAgeError.hasError) {
            const alertMsg = translator(classicVehicleAgeError.errorMessage);
            addErrorAlert(alertMsg, 'classicVehicleAgeError');
        }
        return classicVehicleAgeErrorValue;
    };

    const goToContactUs = () => {
        const contactUsPath = 'contact-us';
        const url = `${window.location.origin}/${contactUsPath}`;
        window.location.href = url;
    }

    const isVehicleNonOwned = (vehicle) => {
        const currentVehicle = vehicle.value ? vehicle.value : vehicle;
        if (currentVehicle.namedNonOwner_ML) {
            goToContactUs()
            return true;
        }
    }

    const isMissingCommute = () => {
        const notEmployedOccupationCodes = ['unemployed', 'retired', 'student'];
        const employedDrivers = paCoverableVal.drivers.find((driver) => {
            return driver.occupation_Ext && !notEmployedOccupationCodes.includes(driver.occupation_Ext);
        });
        const commuteVehicles = paCoverableVal.vehicles.find((vehicle) => {
            return vehicle.primaryUse && (vehicle.primaryUse === 'school_or_work_ml' || vehicle.primaryUse === 'business');
        });

        return employedDrivers && !commuteVehicles;

    }

    const isModifierSelected = (modifiers, modifierCode) => {
        const modifier = modifiers?.find((mod) => mod.codeIdentifier_ML === modifierCode);
        if (modifier) {
            return modifier.chosenTermValue === "true";
        }
        return false;
    };

    const checkForCommuteVehicleAlert = () => {
        const index = alertMessages.findIndex((a) => a.id === alertNames.commutedVehicleName);
        if (index >= 0) {
            alertMessages.splice(index, 1);
        }

        alertMessages.push({
            type: 'warning',
            id: alertNames.commutedVehicleName,
            message: isModernProduct ? translator(messages.noVehiclesForBusinessError) : translator(messages.noVehiclesForCommuteOrBussinessError)
        });

        setCommuteWarningDisplayed(true);
    };


    const removeVinIfNotNeeded = (vehicle) => {
        const categoriesWithoutVin = ['low_speed_vehicle', 'registered_golf_cart', 'trailer'];
        if (categoriesWithoutVin.includes(vehicle.category_ML)) {
            vehicle.vin = undefined;
        }
    };

    const restoreSelectedVehicle = useCallback((lobVM) => {
        if (lobVM.personalAuto && !isAMP) {
            const index = lobVM.personalAuto.coverables.vehicles.value.findIndex((vhc) => nonEditingVehicles.indexOf(vhc.fixedId) === -1);
            const selectedVehicle = lobVM.personalAuto.coverables.vehicles.getElement(index);
            if (selectedVehicle) {
                setCurrentVehicleVM(selectedVehicle);
            }
        }
    }, [isAMP, nonEditingVehicles]);

    const jumpToSelectedStepByStepID = useCallback((newStepID) => {
        MLEndorsementFlowUtil.addAndNavigateToSelectedStep(newStepID, steps, currentStepIndex, changeNextSteps, goNext, productCode)
    }, [changeNextSteps, currentStepIndex, goNext, productCode, steps]);

    const driversVM = submissionVM?.lobData?.personalAuto?.coverables?.drivers;
    const drivers = driversVM?.value || [];
    const primaryVehicleRequiredModifiers = ["PAMatureDriver_Ext", "PADefensiveDriver_Ext", "PADriverTrainingCourse_Ext",];

    const missingPrimaryVehicleAssignment = drivers.some(driverVM => {
        const driver = driverVM?.b || driverVM;
        const modifiers = driver?.driverModifiers_ML?.modifiers || [];
        const hasNoPrimaryVehicle = !driver?.primaryVehicleFixedId || driver.primaryVehicleFixedId === 0;
        const hasSelectedRequiredModifier = modifiers.some(modifier => primaryVehicleRequiredModifiers.includes(modifier.codeIdentifier_ML) &&
            String(modifier.chosenTermValue) === "true"
        );
        return hasNoPrimaryVehicle && hasSelectedRequiredModifier;
    });

    const navigateAfterSave = () => {
        clearAlerts();
        if (isAddOrReplace) {
            const action = isReplace ? 'Replace' : 'Add';
            if (missingPrimaryVehicleAssignment) {
                MLTagManagerService.sendFormStepPolicyChangeStep(`${action} Vehicle`);
                jumpToSelectedStepByStepID("AdditionalPolicyInfoPolChange");
                return;
            }
            MLTagManagerService.sendFormStepPolicyChangeStep(`${action} Vehicle`);
            MLPolicyChangeDataHolderService.setPageAction('requote');
            jumpToSelectedStepByStepID("endorsementCoverages");
        } else {
            MLTagManagerService.sendFormStepPolicyChangeStep(`Edit Vehicle`);
            jumpToSelectedStepByStepID("endorsementAdditionalChanges");
        }
    };

    const syncAndSave = () => {
        const item = currentVehicleVM.value;
        submissionVM.value.portalCurrentStep_ML = currentStep.mlStepName;
        const savePromise = EndorsementService.saveEndorsement([submissionVM.value], authHeader)
        showProgressDialog(messages.save, savePromise);
        savePromise.then((pc) => {
            const policyChange = new PolicyChange(Object.assign({}, pc));
            const updatedEndorsementVM = viewModelService.create(policyChange, 'pc', 'edge.capabilities.policychange.dto.PolicyChangeDataDTO');

            const index = updatedEndorsementVM.lobData.personalAuto.coverables.vehicles.value.findIndex(
                (vhc) => vhc.fixedId === item.fixedId ||
                    vehiclesVal.findIndex((vehicleVal) => vehicleVal.fixedId === vhc.fixedId) === -1);
            const selectedVehicle = updatedEndorsementVM.lobData.personalAuto.coverables.vehicles.getElement(index);
            const details = {
                data: `${item.year} ${item.make} ${item.model}`,
                fixedId: selectedVehicle.fixedId.value,
                replacedFixedId,
                pageAction: MLPolicyChangeDataHolderService.getPageAction()
            };
            MLNavigationStepsTrackingService.completeCurrentStep(details, authHeader);

            restoreSelectedVehicle(updatedEndorsementVM.lobData);
            updateWizardSubmissionData(updatedEndorsementVM);
            const lbl = MLEndorsementUtil.stepLabel(currentStep);
            MLEndorsementUtil.setEndorsementDescription(lbl, updatedEndorsementVM, translator);
            navigateAfterSave();
        });
    };

    const shouldShowAltAddressStateError = (vehicle) => {
        const garagingLocationState = _.get(vehicle, 'garagingLocation_ML.state');

        if (!!garagingLocationState && policyAddress.value.state && garagingLocationState !== policyAddress.value.state) {
            return true;
        }

        return false;
    };

    const handleRegisteredOwnersChange = (selectedDrivers) => {
        setRegisteredOwnersValue(selectedDrivers);
        setCurrentVehicleVM(prevVM => {
            _.set(prevVM, 'registeredOwners.value', selectedDrivers);
            return prevVM
        });
    };

    const checkForStorageVehicle = () => {
        const filteredVehicles = vehicleList.filter(
            (vehicle) =>
                vehicle?.category_ML?.value?.code !== "trailer_np" &&
                vehicle?.category_ML?.value?.code !== "camper_np"
        );

        const isOnlyStorage = filteredVehicles.every(
            (vehicle) => vehicle?.primaryUse?.value?.code === "storage_np"
        );

        return isOnlyStorage;
    };

    const updateRegisteredOwnersIfNotPresent = (registeredOwnersValue) => {
        _.forEach(vehicleList, item => {
            const hasNoOwners =
                !_.has(item, 'registeredOwners') ||
                _.isEmpty(item.registeredOwners.value);
            if (hasNoOwners) {
                _.set(item, 'registeredOwners.value', registeredOwnersValue);
            }
        });
    }

    const onNext = () => {
        const owner = registeredOwnersValue;
        _.set(currentVehicleVM, 'registeredOwners.value', owner)
        updateRegisteredOwnersIfNotPresent(owner);

        if (isAddOrReplace && currentVehicleVM.value.financeCategory_ML === 'owned') {
            currentVehicleVM.value.additionalInterests = [];
        }
        if (hasMissingFields() || shouldShowAltAddressStateError(currentVehicleVM.value)) {
            return false;
        }
        if (!currentVehicleVM.makeAddlInfoReq_Ext) {
            currentVehicleVM.value.makeAddlInfoReq_Ext = false
        }
        currentVehicleVM.hasOnlyTrailers = checkForOnlyTrailer();
        checkForOnlyTrailerAlert();
        if (currentVehicleVM.hasOnlyTrailers) {
            window.scrollTo(0, 0);
            return false;
        }

        if (checkForStorageVehicle()) {
            setErrorMessageForStorageAsTheOnlyVehicle(true)
            window.scrollTo(0, 0);
            return false;
        }

        const vinErrorKey = validateVIN(
            currentVehicleVM.vin.value,
            currentVehicleVM.year?.value,
        );


        if (vinErrorKey) {
            window.scrollTo(0, 0);
            return false;
        }

        if(isModernProduct && currentVehicleVM?.value?.category_ML === 'private_passenger_np' && checkForTrailerCamperEffDate(currentVehicleVM)){
            window.scrollTo(0, 0);
            return false;
        }

        currentVehicleVM.hasDuplicateVins = checkForDuplicateVins(currentVehicleVM.vin.value)
        checkForDuplicateVehicleAlert()
        if (currentVehicleVM.hasDuplicateVins) {
            window.scrollTo(0, 0);
            return false;
        }

        const antiqueVehicleAgeError = onNextValidateAntiqueVehicleAge();
        if (antiqueVehicleAgeError) {
            window.scrollTo(0, 0);
            return false
        }

        const classicVehicleAgeError = onNextvalidateClassicVehicleAge();
        if (classicVehicleAgeError) {
            window.scrollTo(0, 0);
            return false;
        }

        currentVehicleVM.value.toComplete_ML = true;
        if (!(currentVehicleVM.aspects.valid && currentVehicleVM.aspects.subtreeValid
            && !isVehicleNonOwned(currentVehicleVM.value))) {
            return false
        }

        if (currentVehicleVM.errorCounter > 0) {
            return
        }

        const possiblePageActions = ['add', 'edit', 'replace', 'editandreplace']
        if (possiblePageActions.indexOf(MLPolicyChangeDataHolderService.getPageAction()) > 0) {
            checkForDuplicateVehicleAlert();
            if (isMissingCommute() && !commuteWarningDisplayed) {
                if (isAMP) {
                    checkForCommuteVehicleAlert();
                    window.scrollTo(0, 0)
                }
                else {
                    const alertMsg = isModernProduct ? translator(messages.noVehiclesForBusinessError) : translator(messages.noVehiclesForCommuteOrBussinessError);
                    addWarningAlert(alertMsg, 'commuteVehicleAlert')
                    setCommuteWarningDisplayed(true)
                }
                return false;
            }
        }

        if ((currentVehicleVM.value.financeCategory_ML === 'financed' || currentVehicleVM.value.financeCategory_ML === 'leased')
            && _.size(currentVehicleVM.value.additionalInterests) === 0 && MLPolicyChangeDataHolderService.getPageAction() === 'add') {
            setShowErrorMessagetoAddInterestedParty(true);
            window.scrollTo(0, 0);
            return false;
        }

        removeVinIfNotNeeded(currentVehicleVM);
        syncAndSave();

        if (isGPA) {
            PolChangeTrackEvent('Endorsement Step Submitted', false, submissionVM, authHeader, {
                step: 'PolChange Vehicles Screen',
                action: 'Vehicles Submitted'
            });
        }

    }


    const getPageHeader = () => {
        if (isAddOrReplace) {
            if (isReplace) {
                return translator(messages.replaceVehicle)
            }
            return translator(messages.addVehicle)
        }
        return translator(messages.editVehicle)
    }

    const getNextStepText = () => {
        let message = messages.additionalChanges;
        if (isAddOrReplace) {
            message = messages.reviewCoverages;
        }
        return <>{translator(message, { strong: (chunk) => <strong>{chunk}</strong> })}</>
    }

    const _getFixedVehicleIndex = (vehicle) => {
        return paCoverableVal.vehicles.findIndex((elem) => {
            return vehicle.fixedId === elem.fixedId;
        });
    };

    const _findVehicleElement = (vehicle) => {
        return vehicleList.find((veh) => veh.value === vehicle);
    };

    const _getTempVehicleIndex = (vehicle) => {
        return paCoverableVal.vehicles.findIndex((elem) => {
            return vehicle.tempID === elem.tempID;
        });
    };

    const undoVehicleRemoval = (newVehicle) => {
        vehicleList.push(newVehicle);
    };

    const _updateVehicles = (updatedEndorsementVM) => {
        const fixedVehicleIds = [];
        _.get(updatedEndorsementVM, 'lobData.personalAuto.coverables.vehicles.value', []).forEach((vehicle) => {
            if (vehicle.fixedId) {
                fixedVehicleIds.push(vehicle.fixedId);
            }
        });
        return fixedVehicleIds;
    };

    const _saveToEndorsement = () => {
        const promise = EndorsementService.saveEndorsement([submissionVM.value], authHeader);
        showProgressDialog(messages.saveEndorsement, promise)
        return promise;
    };

    const _saveVehicleToEndorsement = () => {

        return _saveToEndorsement().then((policyChangeDTO) => {
            const policyChange = new PolicyChange(policyChangeDTO);
            const updatedEndorsementVM = viewModelService.create(policyChange, 'pc', 'edge.capabilities.policychange.dto.PolicyChangeDataDTO');

            updateWizardSubmissionData(updatedEndorsementVM);

            const fixedVehicleIds = _updateVehicles(updatedEndorsementVM);

            const vhc = _.get(updatedEndorsementVM, 'value.lobData.personalAuto.coverables.vehicles', []).filter((vehicle) => {
                return fixedVehicleIds.indexOf(vehicle.fixedId) > -1;
            });

            MLPolicyChangeDataHolderService.setVehicle(vhc[0]);

            setCurrentVehicleVMInit(updatedEndorsementVM.lobData);

            setReload(true);

            return true;
        }).catch((e) => {
            return e.baseError;
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const setModifierEditMode = useCallback((editMode) => {
        paCoverableVal.vehicles.forEach((vehicle) => {
            if (vehicle.vehicleModifiers_ML) {
                vehicle.vehicleModifiers_ML.forEach((mod) => {
                    mod.editMode_ML = editMode;
                });
            }
        });
    }, [paCoverableVal]);

    const disableValidationErrorForDependentModifiers = useCallback((mod) => {
        if (_.get(mod, 'hasDependentModifier_ML.value')) {
            paCoverableVal.vehicles.forEach((vehicle) => {
                if (!_.isEmpty(vehicle.vehicleModifiers_ML)) {
                    vehicle.vehicleModifiers_ML.forEach((modifier) => {
                        if (modifier.isDependentModifier_ML) {
                            modifier.editMode_ML = false;
                        }
                    });
                }
            });
        }
    }, [paCoverableVal]);

    const getModifierUpdateService = (vm, auth) => {
        return isAMP ? EndorsementService.saveEndorsement(vm, auth) : GatewayEndorsementService.updateModifiers(vm, auth);
    };

    const updateModifiers = useCallback((mod, categoryChange) => {
        disableValidationErrorForDependentModifiers(mod);
        setEditModeInit(false);

        const updatePromise = getModifierUpdateService([_.cloneDeep(submissionVM.value)], authHeader);
        showProgressDialog(messages.syncing, updatePromise)
        updatePromise.then((policyChange) => {
            setFixIdsOfNonEditingVehicles(submissionVM.lobData);
            if (categoryChange) {
                policyChange.lobData.personalAuto.coverables.vehicles.forEach((vehicle) => {
                    if (!_.isUndefined(vehicle, 'category_ML') && vehicle.category_ML === 'collectible'
                        && !_.isUndefined(vehicle, 'value_ML.amount') && vehicle.value_ML.amount === 0) {
                        vehicle.value_ML.amount = undefined;
                    }
                });
            }

            submissionVM.value = new PolicyChange(policyChange);
            restoreSelectedVehicle(submissionVM.lobData);
        }, (error) => {
            // eslint-disable-next-line no-console
            hideProgressDialog();
            console.error(error);
        }).finally(() => {
            setEditModeInit(true);
            setModifierEditMode(true);
            updateWizardData(wizardData);
        });
        return updatePromise;
    }, [authHeader, disableValidationErrorForDependentModifiers, restoreSelectedVehicle, setEditModeInit, setFixIdsOfNonEditingVehicles, setModifierEditMode, showProgressDialog, submissionVM, updateWizardData])

    const onEditClick = () => {
        const _removedVehicle = paCoverableVal.vehicles[_getFixedVehicleIndex(currentVehicleVM.value)];
        paCoverableVal.removeVehicle(paCoverableVal.vehicles[_getFixedVehicleIndex(currentVehicleVM.value)]);

        const newVehicle = paCoverableVal.createVehicle();
        newVehicle.category_ML = _removedVehicle.category_ML;
        newVehicle.bodyType_ML = _removedVehicle.bodyType_ML;
        newVehicle.trim_ML = _removedVehicle.trim_ML;
        newVehicle.type_ML = _removedVehicle.type_ML;
        newVehicle.vehicleTypeText_ML = _removedVehicle.vehicleTypeText_ML;
        newVehicle.year = _removedVehicle.year;
        newVehicle.make = _removedVehicle.make;
        newVehicle.makeCode_ML = _removedVehicle.makeCode_ML;
        newVehicle.model = _removedVehicle.model;
        newVehicle.modelCode_ML = _removedVehicle.modelCode_ML;
        newVehicle.vin = _removedVehicle.vin;
        newVehicle.parkedAtPrimary_ML = true;
        newVehicle.isSelected = true;
        newVehicle.isReplacing = false;
        // Not part of the original code, workaround for using the wrong vehicle DTO
        newVehicle.primaryUse = _removedVehicle.primaryUse;
        newVehicle.ownedLessThan6Months_Ext = _removedVehicle.ownedLessThan6Months_Ext;
        newVehicle.parkingType_ML = _removedVehicle.parkingType_ML;
        newVehicle.purchaseDate_ML = _removedVehicle.purchaseDate_ML;
        newVehicle.financeCategory_ML = _removedVehicle.financeCategory_ML;
        // End workaround
        newVehicle.error = {
            message: undefined
        };
        newVehicle.editMode_ML = true;
        newVehicle.isAnnualMileageRequired_ML = false;
        MLPolicyChangeDataHolderService.setPageAction('editandreplace');
        MLPolicyChangeDataHolderService.setVehicle(_removedVehicle);

        const vehicleVM = _findVehicleElement(newVehicle);
        setCurrentVehicleVM(vehicleVM);

        const onCancelEditVehicle = () => {
            undoVehicleRemoval(newVehicle);
            paCoverableVal.removeVehicle(paCoverableVal.vehicles[_getTempVehicleIndex(newVehicle)]);
            setCurrentVehicleVMInit(submissionVM.lobData);
        }

        const onSaveVehicle = () => {
            if (vehicleVM.aspects.valid && vehicleVM.aspects.subtreeValid) {
                paCoverableVal.removeVehicle(newVehicle);
                paCoverableVal.addVehicle(vehicleVM);
                return _saveVehicleToEndorsement();
            }
        }

        const componentProps = {
            title: messages.EditVehicle,
            subtitle: messages.SecondModalTitle,
            CustomComponent: MLPartialVehicleDetailsComponent,
            viewModel: vehicleVM,
            showFooterOnCustomComponent: true,
            actionBtnLabel: translator(commonMessages.mlContinue),
            overrideProps: {
                submissionVM: submissionVM,
                showProgressDialog,
                hideProgressDialog,
                isCompRaterQuote: false,
                viewModelService,
                updateVinErrorCheck,
                vinErrorCheck,
            }
        };

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).then(() => {
            onSaveVehicle();
        }).catch(onCancelEditVehicle);

    }

    const getMLPaEditVehicleComponent = () => {
        if (isGPA) {
            return isloaded ? MLPAAgentEditVehicleComponent : React.Fragment;
        }
        return isloaded ? MLPAEditVehicleComponent : React.Fragment;
    }

    const validateVIN = (vin, modelYear) => {
        const vinValue = vin?.toUpperCase() || '';

        setVinInvalidErrorMessage(null);
        setVinNinthCharErrorMessage(null);
        setVinTenthCharErrorMessage(null);

        const shouldValidate = vinValue.length === 17 && Number(modelYear) >= 1981;
        if (!shouldValidate) return false;

        let hasAnyError = false;

        // VBR.077
        if (/[IOQ]/.test(vinValue)) {
            setVinInvalidErrorMessage(messages.vinInvalidCharactersError);
            hasAnyError = true;
        }

        // VBR.078
        const ninthChar = vinValue.charAt(8);
        if (!/^[0-9X]$/.test(ninthChar)) {
            setVinNinthCharErrorMessage(messages.vinNinthCharacterError);
            hasAnyError = true;
        }

        // VBR.079
        const tenthChar = vinValue.charAt(9);
        if (['U', 'Z', '0'].includes(tenthChar)) {
            setVinTenthCharErrorMessage(messages.vinTenthCharacterError);
            hasAnyError = true;
        }

        return hasAnyError;
    };

    const checkForTrailerCamperEffDate = (vehicle) => {

        setVehiclePurchaseDateError(null)
        const effectiveDateOfTransaction = submissionVM?.value?.baseData?.effectiveDate
    
        const vehicleCategory = vehicle.value.category_ML
        let hasError = false;
        const effectiveDateOfVehicle = vehicle?.value?.vehiclePurchaseDate

        const vehicleEffectiveDate = new Date(
            effectiveDateOfVehicle.year,
            effectiveDateOfVehicle.month - 1,
            effectiveDateOfVehicle.day
        );
        const policyEffectiveDate = new Date(
            effectiveDateOfTransaction.year,
            effectiveDateOfTransaction.month,
            effectiveDateOfTransaction.day
        );

        hasError = vehicleEffectiveDate > policyEffectiveDate

        if (hasError) {
            if (vehicleCategory === 'private_passenger_np') {
                setVehiclePurchaseDateError(
                    translator(messages.vehiclePurchaseDateError, {
                        year: vehicle.value.year,
                        make: vehicle.value.make,
                        model: vehicle.value.model,
                    })
                )
            } else {
                setVehiclePurchaseDateError(
                    translator(messages.vehiclePurchaseDateErrorForTrailerAndCamper)
                )
            }
        }
 
        return hasError

    }

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            mlpaeditvehicle: getMLPaEditVehicleComponent(),
            mlmodifiercomponent: MLModifierComponent
        },
        resolveCallbackMap: {
            onEditClick,
            onNext
        },
    }
    const overrideProps = {
        addInterestedPartyError: {
            visible: showErrorMessagetoAddInterestedParty
        },
        vehiclePolicyChangePageHeader: {
            visible: isloaded,
            content: getPageHeader()
        },
        ampSubheaderAdd: {
            visible: isAMP && isAddOrReplace && !isReplace
        },
        ampSubheaderEdit: {
            visible: isAMP && !isAddOrReplace
        },
        ampVehicleSummaryHeaderRow: {
            visible: isAMP
        },
        ampVehicleSummaryHeaderItemTitle: {
            content: <><strong>{`${currentVehicleVM?.value?.year} ${currentVehicleVM?.value?.make}`}</strong>{` ${currentVehicleVM?.value?.model} ${currentVehicleVM?.value?.trim_ML}`}</>
        },
        ampVehicleSummaryHeaderVin: {
            content: <>{translator(messages.vin, { number: currentVehicleVM?.value?.vin })}</>
        },
        addNewVehicleDetailsMessage: {
            visible: (!isGPA && isAddOrReplace && !isReplace)
        },
        updateCurrentVehicleDetailsMessage: {
            visible: (!isGPA && !isAddOrReplace)
        },
        vehiclecomponenttable: {
            visible: isloaded,
        },
        paeditvehicleComponent: {
            vehicleVM: currentVehicleVM,
            submissionVM: submissionVM,
            updateModifiers: updateModifiers,
            showValidationErrors: showErrors,
            updateWizardData: updateWizardSubmissionData,
            onValidate,
            isPolicyChange: true,
            isEndorsement: true,
            isAddOrReplace: MLPolicyChangeDataHolderService.getPageAction() === 'add' || MLPolicyChangeDataHolderService.getPageAction() === 'replace',
            authHeader,
            viewModelService,
            showAltAddressStateError: shouldShowAltAddressStateError(currentVehicleVM.value),
            onRegisteredOwnerChange: handleRegisteredOwnersChange
        },
        vehicleModifiers: {
            modifier: _.get(currentVehicleVM, 'vehicleModifiers_ML'),
            isHomeowners: false,
            modifierIdToAppend: _.get(currentVehicleVM, 'tempID.value'),
            visible: isAMP,
            updateModifier: updateModifiers
        },
        nextDescription: {
            content: getNextStepText()
        },
        stickyContinueSection: {
            visible: isGPA,
            defaultContinueSectionId: 'footer',
            nextDescription: getNextStepText(),
            onNext
        },
        vehicleUseStorageError: {
            visible: showErrorMessageForStorageAsTheOnlyVehicle && isModernProduct,
        },
        vinInvalidCharactersError: {
            visible: !!vinInvalidCharactersError,
        },
        vinNinthCharacterError: {
            visible: !!vinNinthCharacterError,
        },
        vinTenthCharacterError: {
            visible: !!vinTenthCharacterError,
        },
        vehiclePurchaseDateErrorNP : {
            visible: !!vehiclePurchaseDateError && isModernProduct,
            messageProps: {
                error: vehiclePurchaseDateError
            }
        }

    }

    if (isAMP) {
        overrideProps.paeditvehicleComponent = {
            isVisible: isloaded,
            readonly: false,
            vehicleVM: currentVehicleVM,
            showValidtionErrors: showErrors,
            handleVehicleModelChange: _.noop(),
            onValidate,
            showVehicleHeader: false,
            policyAddress: policyAddress,
            effectiveDate: effectiveDate,
            isEndorsement: true,
            isAddOrReplace: isAddOrReplace,
            authHeader: authHeader,
            updateViewModel: () => updateWizardData(wizardData)
        }
    }

    return (
        <WizardPage
            showCancel={false}
            showPrevious={false}
            showNext={false}
            policyChangePageHeaderML={isAMP ? messages.vehicles : messages.makeChanges}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrideProps}
                model={wizardData}
                onModelChange={updateWizardData}
                componentMap={resolvers.resolveComponentMap}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                showErrors={showErrors}
                onValidationChange={onValidate}
            />
            <ScrollToError counter={errorTimestamp} />
        </WizardPage>
    )
}
VehiclesPagePolChange.propTypes = wizardProps;
export default VehiclesPagePolChange;